import { useMemo, useState } from 'react'
import {
  CalendarClock,
  ClipboardList,
  Fish,
  GraduationCap,
  History,
  Hourglass,
  KeyRound,
  ListChecks,
  Newspaper,
  RefreshCcwDot,
  RefreshCw,
  ShieldCheck,
  Siren,
  Sparkles,
  Trophy,
  UserRoundPlus,
} from 'lucide-react'
import Button from '../../components/Button'
import FileField from '../../components/FileField'
import { InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import LabelChip from '../../components/LabelChip'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import { DeniedState, EmptyState, ErrorState } from '../../components/States'
import StatusBadge from '../../components/StatusBadge'
import { kit, table } from '../../components/strings'
import { useToast } from '../../components/toastContext'
import PageLoader from '../../components/PageLoader'
import { useResource } from '../../lib/useResource'
import { strings as S } from './strings'
import { getLicense, updateLicense } from './licenseService'
import './LicensePage.css'
import { serverText } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

const num = (value) => (value == null ? S.unavailable : value.toLocaleString('en-US'))

/* The screen's one date shape (DD-MM-YYYY, Taiga #1538); a word the server sends instead of
   a date — "غير محدودة" on an open licence — passes through unchanged. */
const shown = (value) => (value ? displayDate(value) : S.unavailable)

/** The licence type's member name, as the type maps key it ("Number Of Users" -> NumberOfUsers). */
const typeKey = (value) => String(value ?? '').replace(/\s+/g, '')

/* Which limits a type HAS. An open licence has neither an end nor a seat cap, and the server
   prints "unlimited" for what a type does not limit — the history says the same instead of
   the placeholder figures (9,000 days) such a licence carries. */
const TIMED = new Set(['TimeStamp', 'NumberOfUsersAndTimeStamp'])
const SEATED = new Set(['NumberOfUsers', 'NumberOfUsersAndTimeStamp'])

/** Start + duration, as an ISO date — the history row's end, which the payload does not carry. */
const endOf = (start, days) => {
  if (!start || !days) return ''
  const [y, m, d] = start.split('-').map(Number)
  const end = new Date(y, m - 1, d + days)
  const pad = (n) => String(n).padStart(2, '0')
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`
}

/** How much of a whole is spent, 0–100, or null when either half is unknown. */
const spent = (used, total) => (used == null || !total ? null : Math.min(100, (used / total) * 100))

/**
 * The bar's own verdict on itself: it starts green and darkens as it fills.
 * Past 60% consumed somebody should look; past 90% the licence is effectively
 * spent and somebody has to act. Seats and days share the scale — a licence at
 * 92% of its seats is the same finding as one 92% through its term.
 */
const toneFor = (percentUsed) => {
  if (percentUsed == null) return 'neutral'
  if (percentUsed > 90) return 'danger'
  if (percentUsed > 60) return 'warning'
  return 'success'
}

/**
 * الرخصة — Views/AdminLicenseManagement/Index.cshtml.
 *
 * The legacy prints eleven fields as eleven equal cells and leaves the reader to
 * work out which of them is the one that matters. Two of them are: how many DAYS
 * are left, and how many SEATS are left. Both are fractions of a whole the page
 * already knows, so each reads as a remainder over a meter — the figure somebody
 * acts on, the bar saying how close it is to the end, and the flat facts
 * (start, expiry / consumed, total) demoted under the mark they explain.
 * Everything else — client, type, operation — is identity, so it sits in the
 * card's head rather than competing as a tile.
 *
 * The licence blob in the demo database is encrypted, so a field with nothing
 * behind it renders the legacy placeholder and its meter shows an empty track.
 */
/**
 * The eight licence features, drawn with the icon the SIDEBAR uses for the same area.
 *
 * A licence grants صياد or السياسات; the reader recognises those by the mark beside them in
 * the navigation, so repeating it here costs nothing and makes the list scannable rather
 * than eight lines of text. Keys are the `LicenseFeatureSet` member names, including the
 * enum's own misspelling of Gamification.
 */
const FEATURE_ICONS = {
  Lms: GraduationCap,
  Policies: ShieldCheck,
  Phishing: Fish,
  SecurityReports: Siren,
  Assessments: ClipboardList,
  AwarenessContent: Newspaper,
  Gamififcation: Trophy,
  Surveys: ListChecks,
}

const FEATURE_ORDER = Object.keys(FEATURE_ICONS)

/**
 * A DELIBERATE tint per licence type, rather than LabelChip's hash of the name.
 *
 * The five types are a scale, not a set of unrelated labels: open grants everything, closed
 * grants nothing, and the three in between limit by time, by seats, or by both. Hashing the
 * name gave `Open` and `Close` whatever colours their letters happened to land on — two
 * opposite meanings with no visual relation. These read in order.
 */
const TYPE_TONES = {
  Open: 0, // emerald — unrestricted
  Close: 7, // slate — inert; the one type that is never valid
  TimeStamp: 2, // sky — bounded by time
  NumberOfUsers: 4, // violet — bounded by seats
  NumberOfUsersAndTimeStamp: 3, // indigo — bounded by both
}

/** The operation, tinted by what it does to the licence you already have. */
const OPERATION_META = {
  new: { icon: Sparkles, tone: 'new' },
  update: { icon: RefreshCcwDot, tone: 'update' },
  renew: { icon: RefreshCw, tone: 'renew' },
  'public-key': { icon: KeyRound, tone: 'key' },
}

export default function LicensePage() {
  const toast = useToast()
  /* Keeps the last licence on screen while a reload runs, so applying a term refreshes the
     figures in place rather than flashing the loader. */
  const { data: license, error: loadError, reload } = useResource((signal) => getLicense(signal), [])
  const [updateOpen, setUpdateOpen] = useState(false)
  const [file, setFile] = useState(null)
  /* What the picked file SAYS, read before anything is sent. Null until a file parses. */
  const [preview, setPreview] = useState(null)
  const [renewOption, setRenewOption] = useState('no-copy')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Reopening on a previous file's preview would invite applying the wrong licence twice.
  const closeUpdate = () => {
    setUpdateOpen(false)
    setFile(null)
    setPreview(null)
    setError('')
    setRenewOption('no-copy')
  }

  /* The terms newest first. The payload does not flag the active one, so it is the row whose
     start, duration and seats match the licence the server reports as current — on a tie of
     dates that also puts it first. */
  const history = useMemo(() => {
    if (!license) return []
    const isCurrent = (row) =>
      row.startDate === license.startDate &&
      row.duration === license.duration &&
      row.usersNumber === license.usersNumber
    return license.history
      .map((row) => ({ ...row, current: isCurrent(row) }))
      .sort((a, b) => b.startDate.localeCompare(a.startDate) || b.current - a.current)
  }, [license])

  /**
   * Applying a new licence term.
   *
   * The endpoint takes the document's TEXT, not an upload, so the file is read here. The
   * modal stays open on failure — the licence is the thing that decides how many people
   * can use the product, and closing it over a message nobody read is how a failed renewal
   * gets mistaken for a successful one.
   */
  /**
   * READ THE FILE WHEN IT IS PICKED, not when it is submitted.
   *
   * The legacy screen does the same and for the same reason: a licence decides how many
   * people can use the product and for how long, and the administrator applying one should
   * see whose licence it is and what it grants BEFORE it replaces the current term. It also
   * catches the two mistakes that otherwise only surface as a server refusal — the wrong
   * file entirely, and a `new` licence being applied where an update belongs.
   *
   * The submit stays disabled until a file parses, exactly as the legacy disables it.
   */
  const pick = async (selected) => {
    setFile(selected)
    setPreview(null)
    setError('')

    if (!selected) return

    let parsed
    try {
      parsed = JSON.parse(await selected.text())
    } catch {
      setError(S.update.notJson)
      return
    }

    const info = parsed?.LicenseInfo
    if (!info || typeof info !== 'object' || Array.isArray(info)) {
      setError(S.update.noLicenseInfo)
      return
    }

    setPreview({
      clientName: info.ClientName ?? '',
      operationType: String(info.OperationType ?? '').toLowerCase(),
      licenseType: info.LicenseType ?? '',
      startDate: info.StartDate ?? '',
      usersNumber: info.NumberOfUsers ?? 0,
      duration: info.Duration ?? 0,
      featureSet: info.FeatureSet ?? '',
    })
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!file || !preview) {
      setError(S.update.emptyFile)
      return
    }

    setSubmitting(true)
    setError('')

    let content
    try {
      content = await file.text()
    } catch {
      setSubmitting(false)
      setError(S.update.emptyFile)
      return
    }

    const outcome = await updateLicense({
      file: content,
      // Only a RENEWAL has anything to carry over; an update keeps the licence it edits.
      copyOldUsers: preview.operationType === 'renew' && renewOption === 'copy',
    })
    setSubmitting(false)

    if (!outcome.ok) {
      // The server's own words: which key failed to verify, or why the term was refused.
      setError(outcome.message || S.update.failed)
      return
    }

    setUpdateOpen(false)
    setFile(null)
    setPreview(null)
    toast.success(serverText(outcome.message) || S.update.success)
    // The seat counts and the expiry all move when a term is applied.
    reload()
  }

  /* A 403 is told apart from a failure: one asks for an administrator, the other for a
     retry. Only a failure with nothing loaded yet replaces the page. */
  if (!license) {
    return (
      <>
        <PageHeader title={S.title} />
        {!loadError ? (
          <PageLoader />
        ) : loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState
            title={table.loadFailed}
            action={
              <Button variant="soft" onClick={reload}>
                {kit.retry}
              </Button>
            }
          />
        )}
      </>
    )
  }

  const daysUsed =
    license.daysLeft == null || !license.duration
      ? null
      : Math.min(100, ((license.duration - license.daysLeft) / license.duration) * 100)
  const seatsUsed = spent(license.consumedAccounts, license.usersNumber)

  /* The verdict beside the client, from the days alone — seats running out limits who can
     be added, not whether the licence holds. Nothing is claimed when the term is unknown. */
  const state =
    daysUsed == null
      ? null
      : license.daysLeft <= 0
        ? { tone: 'danger', label: S.state.expired }
        : toneFor(daysUsed) === 'danger'
          ? { tone: 'warning', label: S.state.expiring }
          : { tone: 'success', label: S.state.active }

  /* The features the file GRANTS, or null for "not limited to any". `FeatureSet` is a
     ';'-separated list of `LicenseFeatureSet` names; the generator normalises the separator
     and the product splits on it the same way. */
  const previewGranted = !preview?.featureSet?.trim()
    ? null
    : new Set(
        preview.featureSet
          .split(';')
          .map((name) => name.trim())
          .filter(Boolean),
      )

  const operation = OPERATION_META[preview?.operationType] ?? OPERATION_META.update

  /* The term line wears the badge's verdict, not a scale of its own: a line that turned amber
     at 60% while the badge still said "active" — or red while it said "expiring" — gave two
     answers to one question. The seats keep the graded scale; they have no badge. */
  const daysTone = state?.tone ?? 'neutral'
  const termRunning = daysUsed != null && license.daysLeft > 0
  const seatsTone = toneFor(seatsUsed)
  const seatsLeft = seatsUsed == null ? null : Math.max(0, 100 - seatsUsed)

  /* "متبقي 302 يوم" / "302 days left", split around the number so the figure can be set
     larger than its words without a second string per language. */
  const [leftBefore, leftAfter] = S.term.left('\u0000').split('\u0000')

  return (
    <>
      <PageHeader
        title={S.title}
        actions={
          <Button onClick={() => setUpdateOpen(true)}>
            <RefreshCw size={16} strokeWidth={1.75} />
            {S.update.trigger}
          </Button>
        }
      />

      {/* THE LICENCE. A licence is a period and a pool, so the card draws one of each: the
          term as a line in time — start, today, expiry — and the seats as a ring beside it.
          Green is what is left on both; it turns amber, then red, as either runs out. */}
      <section className="lic-hero" aria-label={S.subtitle}>
        <div className="lic-hero__main">
          {/* Whose licence and whether it holds — the verdict beside the name it is about.
              Its type is in the history below. */}
          <header className="lic-hero__head">
            <div className="lic-hero__name">
              <h2 className="lic-hero__client">
                <bdi>{license.companyName ?? S.unavailable}</bdi>
              </h2>
              {state && <StatusBadge tone={state.tone}>{state.label}</StatusBadge>}
            </div>
          </header>

          {/* THE TERM. The track is the whole licence; the part already lived is quiet, the
              part still to come carries the verdict colour, and today sits where they meet
              with the count left pinned above it. */}
          <div
            className={`lic-term lic-term--${daysTone}${termRunning ? '' : ' lic-term--still'}`}
            style={{ '--elapsed': `${daysUsed ?? 0}%` }}
          >
            {/* No callout once the term has run out: "0 days left", or a negative count, is a
                figure to work out, and the badge already gives the verdict. */}
            {termRunning && (
              <p className="lic-term__callout">
                {leftBefore.trim() && <bdi className="lic-term__words">{leftBefore.trim()}</bdi>}
                <span className="lic-term__days tabular" dir="ltr">
                  {num(license.daysLeft)}
                </span>
                {leftAfter.trim() && <bdi className="lic-term__words">{leftAfter.trim()}</bdi>}
              </p>
            )}

            <div
              className="lic-term__track"
              role="progressbar"
              aria-label={S.fields.daysLeft}
              aria-valuemin={0}
              aria-valuemax={license.duration || 0}
              aria-valuenow={license.daysLeft ?? 0}
            >
              <span className="lic-term__past" />
              <span className="lic-term__ahead" />
              {daysUsed != null && <span className="lic-term__today" />}
            </div>

            {/* Each end of the track is ticked, and its date hangs under the tick as one quiet
                line — the track and the callout carry the reading; these only anchor it. */}
            <div className="lic-term__ends">
              {[
                { key: 'near', label: S.fields.startDate, date: license.startDate },
                { key: 'far', label: S.fields.expirationDate, date: license.expirationDate },
              ].map(({ key, label, date }) => (
                <p key={key} className={`lic-term__end lic-term__end--${key}`}>
                  <bdi>{label}</bdi>
                  <span className="lic-term__end-sep" aria-hidden="true" />
                  <span className="lic-term__end-date tabular" dir="ltr">
                    {shown(date)}
                  </span>
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* THE SEATS. The arc is what is left — the figure in its middle — and the gap is
            what has been used; the legend underneath names both in the ring's own colours. */}
        <aside className={`lic-seats lic-seats--${seatsTone}`}>
          <h3 className="lic-seats__title">
            <bdi>{S.seats.title}</bdi>
          </h3>
          <div
            className="lic-ring"
            style={{ '--left': seatsLeft ?? 0 }}
            role="progressbar"
            aria-label={S.fields.remainingAccounts}
            aria-valuemin={0}
            aria-valuemax={license.usersNumber || 0}
            aria-valuenow={license.remainingAccounts ?? 0}
          >
            <svg viewBox="0 0 120 120" aria-hidden="true">
              <circle className="lic-ring__track" cx="60" cy="60" r="52" pathLength="100" />
              {seatsLeft > 0 && <circle className="lic-ring__arc" cx="60" cy="60" r="52" pathLength="100" />}
            </svg>
            <span className="lic-ring__centre">
              <span className="lic-ring__value tabular" dir="ltr">
                {num(license.remainingAccounts)}
              </span>
              {/* "متبقية من 1,500" — the figure is what is LEFT, said in the ring itself; a bare
                  "من 1,500" read as "1,478 used of 1,500", the opposite of the truth. */}
              <span className="lic-ring__scale">
                <bdi>{S.seats.leftOf}</bdi> <span dir="ltr">{num(license.usersNumber)}</span>
              </span>
            </span>
          </div>
          {/* Only the half the ring's centre does not already say. */}
          <dl className="lic-seats__legend">
            {[{ key: 'used', label: S.seats.used, value: license.consumedAccounts }].map((part) => (
              <div key={part.key} className={`lic-seats__part lic-seats__part--${part.key}`}>
                <dt>
                  <bdi>{part.label}</bdi>
                </dt>
                <dd className="tabular" dir="ltr">
                  {num(part.value)}
                </dd>
              </div>
            ))}
          </dl>
        </aside>
      </section>

      {/* EVERY TERM THIS INSTALLATION HAS HELD, newest first, on a rail — the current one
          marked, the rest quiet. The four columns a table would give, without the toolbar
          and pager a list this short never needs. */}
      <section className="lic-history" aria-labelledby="lic-history-title">
        {/* The panel's one icon, on a neutral tile, and how many terms the list holds. */}
        <header className="lic-history__head">
          <span className="lic-history__mark" aria-hidden="true">
            <History size={16} strokeWidth={1.75} />
          </span>
          <h2 id="lic-history-title" className="lic-history__title">
            <bdi>{S.history.title}</bdi>
          </h2>
          <span className="lic-history__count tabular">{history.length}</span>
        </header>

        {history.length === 0 ? (
          <EmptyState title={table.zeroRecords} />
        ) : (
          <>
            <div className="lic-history__cols" aria-hidden="true">
              <span />
              <bdi>{S.history.licenseType}</bdi>
              <bdi>{S.history.startDate}</bdi>
              <bdi>{S.history.endDate}</bdi>
              <bdi className="lic-history__num">{S.history.durationDays}</bdi>
              <bdi className="lic-history__num">{S.history.totalAccounts}</bdi>
            </div>

            {/* Each figure carries its own label: read aloud on a wide screen, where the
                column heads show it, and printed above the figure on a narrow one, where
                the heads are gone. */}
            <ol className="lic-history__list">
              {history.map((row, index) => {
                const key = typeKey(row.licenseTypeKey)
                const timed = TIMED.has(key)
                const seated = SEATED.has(key)
                return (
                  <li
                    key={`${row.startDate}-${row.usersNumber}-${index}`}
                    className={`lic-history__row${row.current ? ' lic-history__row--current' : ''}`}
                  >
                    <span className="lic-history__dot" aria-hidden="true" />
                    <span className="lic-history__type">
                      <bdi>{S.licenseTypes[row.licenseTypeKey] ?? row.licenseType}</bdi>
                      {row.current && <StatusBadge tone="primary">{S.history.current}</StatusBadge>}
                    </span>
                    {[
                      { label: S.history.startDate, value: shown(row.startDate), ltr: true },
                      {
                        label: S.history.endDate,
                        value: timed ? shown(endOf(row.startDate, row.duration)) : S.unlimited,
                        ltr: timed,
                      },
                      { label: S.history.durationDays, value: timed ? num(row.duration) : S.unlimited, num: true },
                      { label: S.history.totalAccounts, value: seated ? num(row.usersNumber) : S.unlimited, num: true },
                    ].map((cell) => (
                      <span
                        key={cell.label}
                        className={`lic-history__data${cell.num ? ' lic-history__data--num' : ''}`}
                      >
                        <bdi className="lic-history__label">{cell.label}</bdi>
                        <span className="tabular" {...(cell.ltr ? { dir: 'ltr' } : null)}>
                          {cell.value}
                        </span>
                      </span>
                    ))}
                  </li>
                )
              })}
            </ol>
          </>
        )}
      </section>

      <Modal
        open={updateOpen}
        title={S.update.trigger}
        onClose={closeUpdate}
        footer={
          <Button type="submit" form="license-update-form" disabled={submitting || !preview}>
            {submitting ? S.update.submitting : S.update.submit}
          </Button>
        }
      >
        <form id="license-update-form" className="license__form" onSubmit={submit} noValidate>
          <FormRow label={S.update.fileLabel} error={error} hint={S.update.fileHint}>
            {(id) => (
              <FileField
                id={id}
                name="LicenseFile"
                /* The generator writes `.json`; older files carry `.license`. Both are the
                   same document, so both are offered rather than one being refused. */
                accept=".json,.license,application/json"
                onChange={pick}
              />
            )}
          </FormRow>

          {preview && (
            <section className={`license__preview license__preview--${operation.tone}`}>
              {/* The banner answers the two questions asked in that order: whose licence is
                  this, and what will it do to mine. */}
              <header className="license__preview-head">
                <span className="license__preview-mark" aria-hidden="true">
                  <operation.icon size={18} strokeWidth={1.75} />
                </span>
                <span className="license__preview-who">
                  {/* THE BDI IS INSIDE, not on the styled block. `<bdi>` carries an implicit
                      `dir="auto"`, so a Latin client name in an Arabic panel made the element
                      itself LTR — and `text-align: start` then resolved to the opposite edge
                      from the line beneath it. The span takes the panel's direction and the
                      bdi keeps doing its one job, isolating the name. */}
                  <span className="license__preview-client">
                    <bdi>{preview.clientName}</bdi>
                  </span>
                  <span className="license__preview-op">
                    <bdi>{S.operations[preview.operationType] ?? preview.operationType}</bdi>
                  </span>
                </span>
                <span className="license__preview-type">
                  <LabelChip tone={TYPE_TONES[preview.licenseType]}>
                    {S.licenseTypes[preview.licenseType] ?? preview.licenseType}
                  </LabelChip>
                </span>
              </header>

              <dl className="license__preview-grid">
                {[
                  [CalendarClock, S.update.preview.startDate, preview.startDate],
                  [Hourglass, S.update.preview.duration, S.update.preview.days(preview.duration)],
                  [UserRoundPlus, S.update.preview.usersNumber, preview.usersNumber.toLocaleString('en-US')],
                ].map(([Icon, label, value]) => (
                  <div className="license__preview-row" key={label}>
                    <dt>
                      <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                      <bdi>{label}</bdi>
                    </dt>
                    <dd>
                      <bdi>{value}</bdi>
                    </dd>
                  </div>
                ))}
              </dl>

              {/* EVERY feature, not just the granted ones. A licence is as much about what it
                  withholds, and a list of three chips does not tell you the other five are
                  missing. An empty FeatureSet is not "no features" — the product reads it as
                  unrestricted (`HasFeature` returns true for an empty set), so everything is
                  drawn as included and the caption says why. */}
              <div className="license__features">
                <div className="license__features-head">
                  <bdi className="license__features-title">{S.update.preview.featuresIncluded}</bdi>
                  <bdi className="license__features-count">
                    {previewGranted === null
                      ? S.update.preview.featuresAll
                      : S.update.preview.featuresSome(previewGranted.size, FEATURE_ORDER.length)}
                  </bdi>
                </div>

                <ul className="license__features-list">
                  {FEATURE_ORDER.map((key) => {
                    const Icon = FEATURE_ICONS[key]
                    const on = previewGranted === null || previewGranted.has(key)

                    return (
                      <li
                        className={`license__feature${on ? '' : ' license__feature--off'}`}
                        key={key}
                        title={on ? undefined : S.update.preview.excluded}
                      >
                        <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                        <bdi>{S.features[key] ?? key}</bdi>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {/* The server refuses these two anyway; saying so here saves a round trip and
                  names the file the administrator should have picked instead. */}
              {(preview.operationType === 'new' || preview.operationType === 'public-key') && (
                <InfoNote>
                  <bdi>{S.update.wrongOperation}</bdi>
                </InfoNote>
              )}
            </section>
          )}

          {/* ONLY FOR A RENEWAL. An update keeps the licence it edits, and its users with it,
              so offering to copy them is offering a choice that does not exist — which is how
              the legacy screen behaves too. */}
          {preview?.operationType === 'renew' && (
            <fieldset className="license__options">
              <legend>
                <bdi>{S.update.renewOptions}</bdi>
              </legend>
              {[
                { value: 'no-copy', label: S.update.noCopy },
                { value: 'copy', label: S.update.copy },
              ].map((option) => (
                <label key={option.value} className="license__option">
                  <input
                    type="radio"
                    name="renewOption"
                    value={option.value}
                    checked={renewOption === option.value}
                    onChange={() => setRenewOption(option.value)}
                  />
                  <bdi>{option.label}</bdi>
                </label>
              ))}
            </fieldset>
          )}
        </form>
      </Modal>
    </>
  )
}
